// ABOUTME: One row of cells in the builder. Sortable (drag the grip to reorder it within its section) and a
// ABOUTME: droppable target so columns can be dragged into it. Holds the add-column and remove-row controls.
import { Box, ButtonBase, useTheme } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { tokens } from "../../../../../theme";
import { useDroppable } from "@dnd-kit/core";
import { horizontalListSortingStrategy, SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { RhDragBar } from "./RhDragBar";
import { ReportBuilderColumn } from "./ReportBuilderColumn";
import { dashboardCellKinds, dashboardPivotVariants } from "../dashboardEnums";

// A ButtonBase that can animate in/out via framer-motion while keeping its MUI sx untouched.
const MotionButtonBase = motion.create(ButtonBase);

export const ReportBuilderRow = (props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    // The add-column "+" and remove-row controls fade+pop in/out with row hover instead of snapping.
    const [hovered, setHovered] = useState(false);
    // A row holding a full pivot is locked: no extra columns, and nothing can be dragged into it.
    const rowHasFullPivot = props.row.children.some(
        (c) => c.chart?.kind === dashboardCellKinds.PIVOT && c.chart?.variant === dashboardPivotVariants.FULL
    );

    // The row is both a sortable item (reorder via grip) and a droppable (columns drop into it). Two dnd-kit
    // refs on one node, merged below; the type-filtered collision strategy keeps the two roles from clashing.
    const sortable = useSortable({
        id: props.row.id,
        data: { type: "row", containerKey: props.containerKey, sectionIndex: props.si, dividerIndex: props.di ?? null },
        disabled: rowHasFullPivot, // a full pivot owns its tab — its row never moves
    });
    const droppable = useDroppable({
        id: props.rowPath,
        data: { rowPath: props.rowPath },
        disabled: rowHasFullPivot,
    });
    const setRefs = (node) => { sortable.setNodeRef(node); droppable.setNodeRef(node); };

    const style = {
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
        opacity: sortable.isDragging ? 0.4 : 1,
    };

    // A FULL pivot must own the whole tab (eligibleForFull, decided by the section) and sit alone in a
    // single full-width column.
    const allowFullPivot = !!props.eligibleForFull && props.row.children.length === 1;

    return (
        <SortableContext
            items={props.row.children.map(c => c.id)}
            strategy={horizontalListSortingStrategy}
        >
            <Box
                ref={setRefs}
                style={style}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                sx={{
                    flex: 1,
                    position: "relative",
                    zIndex: 3, // sit above the section's drag bar so this row's own bar wins on hover
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {/* Top accent bar = grab to move this row. Right-inset past the add-column button. Locked for a full pivot. */}
                {!rowHasFullPivot &&
                    <RhDragBar
                        activatorRef={sortable.setActivatorNodeRef}
                        attributes={sortable.attributes}
                        listeners={sortable.listeners}
                        right={0}
                        zIndex={4}
                        accent={colors.orange[400]}
                    />
                }

                <Box
                    sx={{
                        display: "flex",
                        gap: 2,
                        height:1,
                        position:"relative",
                    }}
                >
                    {props.row.children.map((column,ci) => (
                        <ReportBuilderColumn
                            key={column.id}
                            column={column}

                            handleRemoveColumn={props.handleRemoveColumn}
                            handleResizeColumn={props.handleResizeColumn}

                            onOpenReportPicker={props.onOpenReportPicker}
                            handleSetColumnChart={props.handleSetColumnChart}
                            allowFullPivot={allowFullPivot}
                            dragDisabled={rowHasFullPivot}

                            containerKey={props.containerKey}
                            si={props.si}
                            di={props.di}
                            ri={props.ri}
                            ci={ci}
                        />
                    ))}
                    <AnimatePresence>
                        {!rowHasFullPivot && hovered &&
                        <MotionButtonBase
                            key="add-column-btn"
                            className="add-column-btn"
                            onClick={() => props.handleAddColumn(props.containerKey, props.si, props.ri, props.di)}
                            initial={{ opacity: 0, scale: 1 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 1 }}
                            transition={{ duration: 0.15 }}
                            sx={{
                                position:'absolute',
                                right:8,
                                top:8,
                                bottom:8,
                                minHeight: 200,
                                width: 32,
                                borderRadius: 4,
                                background: theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[100],
                                "&:hover": {
                                    background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[200],
                                }
                            }}
                        >
                            +
                        </MotionButtonBase>
                        }
                    </AnimatePresence>
                </Box>

                {/* Floats over the bottom of the columns above it (absolute) instead of pushing layout down.
                    Trash icons anchored at both ends so the action stays clear when scanning the edges. */}
                <AnimatePresence>
                    {hovered &&
                    <MotionButtonBase
                        key="remove-row-btn"
                        className="remove-row-btn"
                        onClick={() => props.handleRemoveRow(props.containerKey, props.si, props.ri, props.di)}
                        initial={{ opacity: 0, scale: 1 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 1 }}
                        transition={{ duration: 0.15 }}
                        sx={{
                            position: "absolute",
                            bottom: 8,
                            left: 8,
                            right: 64,
                            zIndex: 2,
                            height: 32,
                            px: 2,
                            borderRadius: 4,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            background: colors.red[400],
                            color: theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["white"],
                            boxShadow: "0px 2px 8px rgba(0,0,0,0.15)",
                            "&:hover": {
                                filter: "brightness(0.93)"
                            }
                        }}
                    >
                        <DeleteOutlinedIcon fontSize="small" />
                        {t("remove_row")}
                        <DeleteOutlinedIcon fontSize="small" />
                    </MotionButtonBase>
                    }
                </AnimatePresence>
            </Box>
        </SortableContext>
    );
};
